import cx from "classnames";
import React from "react";

import ProductFacets from "@/mk2/apps/bazaar/components/ProductFacets";
import { ProductQuality } from "@/mk2/apps/bazaar/schemas";
import { StrollerEntity, StrollerPhoto } from "@/mk2/apps/strollers/schemas";
import { strollersDetailUrl, strollersUrl } from "@/mk2/apps/strollers/urls";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { STROLLER_STROLLEROPEDIA } from "@/mk/autogenerated/translations/GridStrollerItem.eb21fdfa8e548cb5276224ed8d250f61";
import { QUALITY_VALUES } from "@/mk/bazaar/common/enums";

import stylesGridItemFooter from "./GridItemFooter.mscss";
import stylesGridItemHeader from "./GridItemHeader.mscss";
import stylesGridItemInfo from "./GridItemInfo.mscss";
import stylesGridItemPhoto from "./GridItemPhoto.mscss";
import stylesGridStrollerItem from "./GridStrollerItem.mscss";

interface OwnProps {
    className?: string;
    stroller: StrollerEntity;
    style?: React.CSSProperties;
    strollerPhotosMap: Record<number, StrollerPhoto>;
    gridIndex: number;
    onClick?(stroller: StrollerEntity, variantId: number, gridIndex: number);
}

type Props = OwnProps;

const GridStrollerItem: React.FunctionComponent<Props> = ({
    className,
    stroller,
    strollerPhotosMap,
    gridIndex,
    onClick,
}) => {
    const detailUrl = url(strollersDetailUrl, { slug: stroller.slug });

    let firstPhoto = null;
    if (stroller?.previewPhoto) {
        firstPhoto = stroller.previewPhoto;
    }

    const onClickCallback = useEventCallback(() => {
        if (onClick) {
            onClick(stroller, null, gridIndex);
        }
    }, [onClick, stroller]);

    return (
        <div
            className={cx(stylesGridStrollerItem.GridStrollerItem, className)}
            data-cy="bazaar-grid-item"
        >
            <Link to={detailUrl} onClick={onClickCallback}>
                <div
                    className={cx(
                        stylesGridItemHeader.GridItemHeader,
                        stylesGridItemHeader[`GridItemHeader--${ProductQuality.NEW}`],
                    )}
                >
                    <span
                        className={cx(
                            stylesGridItemHeader.GridItemHeader__quality,
                            stylesGridItemHeader[`GridItemHeader__quality--${ProductQuality.NEW}`],
                        )}
                    >
                        {QUALITY_VALUES[ProductQuality.NEW].label}
                    </span>
                </div>
            </Link>
            <Link
                to={detailUrl}
                className={stylesGridStrollerItem.GridStrollerItem__link}
                onClick={onClickCallback}
            >
                <div className={stylesGridItemInfo.GridItemInfo}>
                    <div className={stylesGridItemInfo.GridItemInfo__content}>
                        <div
                            className={stylesGridItemInfo.GridItemInfo__title}
                            title={stroller.name}
                        >
                            {stroller.name}
                            <div className={stylesGridItemInfo.GridItemInfo__mask} />
                        </div>

                        <div className={stylesGridItemInfo.GridItemInfo__props}>
                            <ProductFacets
                                className={stylesGridItemInfo.GridItemInfo__facets}
                                facets={[stroller.brand.name, stroller.name]}
                                small
                            />
                            <div className={stylesGridItemInfo.GridItemInfo__mask} />
                        </div>
                    </div>
                </div>
            </Link>
            <Link to={detailUrl} onClick={onClickCallback}>
                <div className={cx(stylesGridItemPhoto.GridItemPhoto)}>
                    <div className={stylesGridItemPhoto.GridItemPhoto__box}>
                        {firstPhoto && (
                            <ImgCropped
                                imgUrl={firstPhoto.url720x720}
                                lazy={false}
                                imgWidth={firstPhoto.originalWidth}
                                imgHeight={firstPhoto.originalHeight}
                                mode={ImgCropMode.Embed}
                                width={233}
                                height={221}
                            />
                        )}
                    </div>
                </div>
            </Link>
            <div className={cx(stylesGridItemFooter.GridItemFooter)}>
                <div className={stylesGridItemFooter.GridItemFooter__first}>
                    <Link
                        to={strollersUrl}
                        className={stylesGridItemFooter.GridItemFooter__username}
                    >
                        {STROLLER_STROLLEROPEDIA}
                    </Link>
                </div>
            </div>
        </div>
    );
};

GridStrollerItem.displayName = "GridStrollerItem";

export default React.memo<Props>(GridStrollerItem);
